import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

import SidebarPageLayout from './SidebarPageLayout';
import config from './config';
import RespiteHelpModal from './Components/RespiteHelpModal';

const RESPITE_TYPES = [
  { value: 'family', label: 'Family member' },
  { value: 'friend', label: 'Friend or neighbor' },
  { value: 'adult_day', label: 'Adult day center' },
  { value: 'in_home_aide', label: 'In-home aide' },
  { value: 'other', label: 'Other' },
];

const TYPE_LABELS = RESPITE_TYPES.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

// Plain YYYY-MM-DD helpers. Dates are handled as local calendar days so the
// rolling window never shifts because of time zones.
const toPlainDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parsePlainDate = (value) => {
  const [year, month, day] = String(value || '').split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const formatPlainDate = (value) =>
  parsePlainDate(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Builds the day cells for a month, padded with blanks so the first day lands
// under the correct weekday column.
const buildMonthCells = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = Array.from({ length: firstWeekday }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(toPlainDate(new Date(year, month, day)));
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
};

const getStatusChipClass = (goalMet) =>
  goalMet
    ? 'bg-[#e5f6ea] text-[#1f7a3b] border-[#b8e0c3]'
    : 'bg-[#fde8e8] text-[#9b2424] border-[#f3b8b8]';

function LogRespiteModal({ initialDate, onClose, onSave, isSaving }) {
  const today = useMemo(() => toPlainDate(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(initialDate || today);
  const [selectedType, setSelectedType] = useState(null);
  const [note, setNote] = useState('');

  const canSave = Boolean(selectedDate) && selectedType != null && !isSaving;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto vica-scrollbar bg-[#0f2b3a]/55"
      role="dialog"
      aria-modal="true"
      aria-labelledby="log-respite-title"
    >
      <div className="flex min-h-full items-center justify-center px-4 py-6">
      <div className="relative w-full max-w-3xl rounded-[18px] bg-white shadow-[0_24px_70px_rgba(0,52,80,0.28)] border border-[#cfe2ee]">
        <button
          type="button"
          className="absolute right-4 top-4 h-12 w-12 rounded-full border border-white/40 bg-white/15 text-white text-2xl leading-none font-bold"
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          x
        </button>

        <div className="bg-gradient-to-br from-[#004f79] via-[#006298] to-[#2a8bc7] px-8 py-7 pr-20 text-white">
          <h2 id="log-respite-title" className="m-0 text-[2.2rem] font-extrabold">
            Log a break
          </h2>
          <p className="m-0 mt-2 text-[1.25rem] leading-8 text-[#e8f6ff]">
            Record a day when you had 8 consecutive hours away from caregiving.
          </p>
        </div>

        <div className="px-8 py-7 text-[#0f2b3a]">
          <div className="mb-6">
            <div className="mb-3 text-[1.15rem] font-extrabold uppercase tracking-wide text-[#3f6173]">
              When was it?
            </div>
            <input
              type="date"
              className="vica-input !text-[1.3rem] !py-3.5"
              value={selectedDate}
              max={today}
              onChange={(event) => setSelectedDate(event.target.value)}
            />
          </div>

          <div className="mb-5">
            <div className="mb-3 text-[1.15rem] font-extrabold uppercase tracking-wide text-[#3f6173]">
              Who or what covered for you?
            </div>
            <div className="flex flex-wrap gap-3">
              {RESPITE_TYPES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={
                    selectedType === option.value
                      ? 'vica-btn vica-btn-md !text-[1.15rem] !px-5 !py-3'
                      : 'vica-btn-ghost vica-btn-md !text-[1.15rem] !px-5 !py-3'
                  }
                  onClick={() => setSelectedType(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <div className="mb-3 text-[1.15rem] font-extrabold uppercase tracking-wide text-[#3f6173]">
              Notes (optional)
            </div>
            <input
              type="text"
              className="vica-input !text-[1.3rem] !py-3.5"
              placeholder="e.g. My sister stayed with Mom while I went out"
              value={note}
              maxLength={300}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            <button type="button" className="vica-btn-secondary vica-tour-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="vica-btn vica-tour-btn-primary"
              disabled={!canSave}
              onClick={() =>
                onSave({
                  date: selectedDate,
                  type: selectedType,
                  note: note.trim(),
                })
              }
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

function RespiteCalendar({ logsByDate, windowStart, windowEnd, onSelectDay }) {
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const today = useMemo(() => toPlainDate(new Date()), []);
  const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);

  const now = new Date();
  const isCurrentMonth =
    viewMonth.getFullYear() === now.getFullYear() && viewMonth.getMonth() === now.getMonth();

  const shiftMonth = (offset) =>
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));

  return (
    <div className="w-full rounded-xl border border-[#dceaf2] bg-white px-5 py-5">
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          className="vica-btn-secondary vica-btn-md !text-[1.1rem]"
          onClick={() => shiftMonth(-1)}
          aria-label="Previous month"
        >
          Back
        </button>
        <div className="text-[1.5rem] font-extrabold text-[#0f3950]">
          {viewMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </div>
        <button
          type="button"
          className="vica-btn-secondary vica-btn-md !text-[1.1rem]"
          onClick={() => shiftMonth(1)}
          disabled={isCurrentMonth}
          aria-label="Next month"
        >
          Next
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="pb-1 text-center text-[1rem] font-extrabold uppercase tracking-wide text-[#6b8798]"
          >
            {label}
          </div>
        ))}

        {cells.map((date, index) => {
          if (!date) {
            return <div key={`blank-${index}`} />;
          }

          const log = logsByDate[date];
          const isFuture = date > today;
          const isToday = date === today;
          const inWindow = date >= windowStart && date <= windowEnd;

          let cellClass = 'border border-[#e3eef5] bg-white text-[#4f6f80] hover:bg-[#f0f9ff]';
          if (log) {
            cellClass = 'border border-[#2f9e5e] bg-[#2f9e5e] text-white hover:bg-[#278a51]';
          } else if (isFuture) {
            cellClass = 'border border-transparent bg-transparent text-[#c3d4de] cursor-default';
          } else if (inWindow) {
            cellClass = 'border border-[#b7ddf1] bg-[#eaf6fd] text-[#0f4e71] hover:bg-[#dff0fc]';
          }

          return (
            <button
              key={date}
              type="button"
              disabled={isFuture}
              onClick={() => onSelectDay(date, log)}
              title={
                log
                  ? `${formatPlainDate(date)}: 8 consecutive hours (${TYPE_LABELS[log.type] || 'Other'})`
                  : formatPlainDate(date)
              }
              className={`flex h-20 items-center justify-center rounded-lg text-[1.35rem] font-extrabold transition ${cellClass} ${
                isToday ? 'ring-2 ring-[#006298] ring-offset-1' : ''
              }`}
            >
              {parsePlainDate(date).getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[1.2rem] font-semibold text-[#4f6f80]">
        <span className="flex items-center gap-2">
          <span className="inline-block h-4 w-4 rounded bg-[#2f9e5e]" />
          Break logged
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block h-4 w-4 rounded border border-[#b7ddf1] bg-[#eaf6fd]" />
          Current 7 day window
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block h-4 w-4 rounded ring-2 ring-[#006298]" />
          Today
        </span>
      </div>

      <p className="mb-0 mt-3 text-[1.2rem] font-semibold text-[#6b8798]">
        Tap any past day to add or review a break.
      </p>
    </div>
  );
}

function RespiteRecordPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const userId = localStorage.getItem('userId') || '';

  // One break per day, so a date-keyed lookup is enough for the calendar.
  const logsByDate = useMemo(() => {
    const map = {};
    logs.forEach((log) => {
      if (log?.date) {
        map[log.date] = log;
      }
    });
    return map;
  }, [logs]);

  const openModal = (date) => {
    setModalDate(date || null);
    setSelectedLog(null);
    setModalOpen(true);
  };

  const handleSelectDay = (date, log) => {
    if (log) {
      setSelectedLog(log);
      return;
    }
    openModal(date);
  };

  const fetchLogs = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setError('We could not find your account. Please sign in again.');
      return;
    }

    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/respite-logs`, {
        params: { userId, today: toPlainDate(new Date()) },
      });
      setLogs(response.data?.logs || []);
      setSummary(response.data?.summary || null);
      setError('');
    } catch (err) {
      console.error('Fetch respite logs error:', err);
      setError('We could not load your respite entries. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSave = async (entry) => {
    setIsSaving(true);
    try {
      await axios.post(`${config.API_BASE_URL}/api/respite-logs`, { userId, ...entry });
      setModalOpen(false);
      await fetchLogs();
    } catch (err) {
      console.error('Save respite log error:', err);
      setError('We could not save that entry. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (logId) => {
    try {
      await axios.post(`${config.API_BASE_URL}/api/respite-logs/delete`, { userId, logId });
      setSelectedLog(null);
      await fetchLogs();
    } catch (err) {
      console.error('Delete respite log error:', err);
      setError('We could not remove that entry. Please try again.');
    }
  };

  const goalHours = summary?.goalHours ?? 8;
  const goalMet = Boolean(summary?.goalMet);

  return (
    <SidebarPageLayout>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="vica-title !text-[2.2rem]">Respite Record</h1>
          <p className="vica-subtitle !text-[1.3rem] leading-8">
            Taking a real break is the foundation of caring for yourself.
          </p>
        </div>
        <button
          className="vica-btn-secondary vica-session-btn-new shrink-0 justify-center"
          onClick={() => navigate('/home')}
        >
          <ArrowBackIcon sx={{ fontSize: 28 }} />
          Back
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-[#f3b8b8] bg-[#fde8e8] px-4 py-3 text-[1.15rem] font-semibold text-[#9b2424]">
          {error}
        </div>
      )}

      {loading ? (
        <div className="vica-empty-state">Loading your respite record...</div>
      ) : (
        <>
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="flex flex-col rounded-xl border border-[#cfe2ee] bg-[#f7fbfe] px-5 py-5">
              <div className="text-[1.5rem] leading-9 font-extrabold text-[#3f6173]">
                Have you taken {goalHours} consecutive hours away from caregiving in the last 7 days?
              </div>
              <div className="mt-2 text-[2.6rem] leading-tight font-extrabold text-[#0f3950]">
                {goalMet ? 'Yes' : 'Not yet'}
              </div>
              <div className="mt-2 text-[1.25rem] font-semibold text-[#4f6f80]">
                {goalMet && summary?.lastBreakDate
                  ? `Most recent break: ${formatPlainDate(summary.lastBreakDate)}`
                  : 'No break logged in the last 7 days'}
              </div>
              {summary && (
                <div className="mt-1 text-[1.1rem] font-semibold text-[#6b8798]">
                  {formatPlainDate(summary.windowStart)} to {formatPlainDate(summary.windowEnd)}
                </div>
              )}

              <div className="mt-4">
                <span className={`vica-tag border !text-[1.2rem] !leading-7 !py-2 !px-4 ${getStatusChipClass(goalMet)}`}>
                  {goalMet
                    ? 'Goal reached. Wonderful work taking care of yourself.'
                    : 'Try to find one unbroken 8 hour stretch this week.'}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <button className="vica-btn vica-session-btn-new" onClick={() => openModal(null)}>
                  <AddCircleOutlineIcon sx={{ fontSize: 28 }} />
                  Log a break
                </button>
                <button
                  className="vica-btn-secondary vica-session-btn-new"
                  onClick={() => setHelpOpen(true)}
                >
                  <HelpOutlineIcon sx={{ fontSize: 28 }} />
                  Need help?
                </button>
              </div>

              <div className="mt-5 border-t border-[#dbe8f0] pt-4">
                <h3 className="m-0 mb-3 text-[1.15rem] font-extrabold uppercase tracking-wide text-[#3f6173]">
                  All breaks ({logs.length})
                </h3>

                {logs.length === 0 ? (
                  <p className="m-0 text-[1.25rem] font-semibold text-[#6b8798]">
                    No breaks logged yet.
                  </p>
                ) : (
                  <div className="vica-scrollbar max-h-96 space-y-2 overflow-y-auto pr-1">
                    {logs.map((log) => (
                      <div
                        key={log.id}
                        className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
                          selectedLog?.id === log.id
                            ? 'border-[#2f9e5e] bg-[#f2fbf5]'
                            : 'border-[#dceaf2] bg-white'
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="text-[1.2rem] font-extrabold text-[#0f3950]">
                            {formatPlainDate(log.date)} · {goalHours} consecutive hours
                          </div>
                          <div className="truncate text-[1.05rem] font-semibold text-[#4f6f80]">
                            {TYPE_LABELS[log.type] || 'Other'}
                            {log.note ? ` · ${log.note}` : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="vica-btn-danger vica-btn-md shrink-0 !text-[1.05rem]"
                          onClick={() => handleDelete(log.id)}
                          title="Remove this entry"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <RespiteCalendar
              logsByDate={logsByDate}
              windowStart={summary?.windowStart || ''}
              windowEnd={summary?.windowEnd || ''}
              onSelectDay={handleSelectDay}
            />
          </div>
        </>
      )}

      {helpOpen && <RespiteHelpModal onClose={() => setHelpOpen(false)} />}

      {modalOpen && (
        <LogRespiteModal
          initialDate={modalDate}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}
    </SidebarPageLayout>
  );
}

export default RespiteRecordPage;
